import React, { useEffect, useState } from 'react';
import {
  Heading,
  VStack,
  Text,
  Box,
  HStack,
  Container,
  Icon,
  SimpleGrid,
  GridItem,
  useColorModeValue
} from "@chakra-ui/react";
import { FaMoneyBillWave } from 'react-icons/fa';
import { GoShield } from 'react-icons/go';
import MemberActivityDetails from '../components/member-activity-details';
import ActivityPaymentHistory from '../components/activity-payment-history';
import ProductsToPayList from '../components/products-to-pay-list';
import PaymentMethods from '../components/payment-methods';
import LoadingSpinner from '../components/loading-spinner';
import { useAuth } from '../components/auth';
import { getMember } from '../services/member-service';
import { Activity } from '../models/activity';
import { getActivitiesFeesByMember } from "../services/product-service";
import { ActivityProductPayment } from "../models/activities-fees-by-member-response";
import { HTTP_STATUS_CODE_OK } from "../constants";
import { MemberProductStatus } from "../models/periodic-product-payment";

function MyInsurance(): JSX.Element {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [insuranceActivity, setInsuranceActivity] = useState<Activity | undefined>();
  const [activitiesFeesYears, setActivitiesFeesYears] = useState<string[]>([]);
  const [activitiesFeesPayments, setActivitiesFeesPayments] = useState(new Map<string, ActivityProductPayment[]>());
  const [insuranceFeesToPayPayments, setInsuranceFeesToPayPayments] = useState<MemberProductStatus[]>([]);
  const [subtitleText, setSubtitleText] = useState("");

  const cardBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('green.100', 'green.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    const getMemberInsurance = async (): Promise<void> => {
      try {
        const member = (await getMember(auth?.user?.selectedMember?.id)).response;
        if (member != null) {
          const insurance = member.memberActivities.find((activity) => activity.activity.isInsurance)?.activity;

          if (insurance) {
            setInsuranceActivity(insurance);
            setSubtitleText("Acá podés visualizar y abonar tu seguro.");
          } else {
            setSubtitleText("Actualmente no tenés un seguro. Nada de que preocuparse: esto se debe a que no estás dado de alta en ninguna actividad que requiera seguro.");
          }

          const activitiesFeesResponse = await getActivitiesFeesByMember(member.id);
          if (activitiesFeesResponse.statusCode === HTTP_STATUS_CODE_OK && activitiesFeesResponse.response) {
            const keys = Object.keys(activitiesFeesResponse.response.activityFeesPayments);
            const entries = Object.entries(activitiesFeesResponse.response.activityFeesPayments);
            const map = new Map(entries);
            setActivitiesFeesYears(keys.reverse());
            setActivitiesFeesPayments(map);
          }
        }
      } finally {
        setIsLoading(false);
      }
    }

    getMemberInsurance().catch(() => setIsLoading(false));
  }, [auth?.user?.selectedMember?.id]);

  const handleCheckboxChange = (insuranceActivityId: string, periodicProductCheckbox: MemberProductStatus) => {
    const index = insuranceFeesToPayPayments.findIndex((periodicProductPayment) => periodicProductPayment.product.id === periodicProductCheckbox.product.id);

    if (index >= 0) {
      setInsuranceFeesToPayPayments(insuranceFeesToPayPayments.filter((ppp) => ppp.product.id !== periodicProductCheckbox.product.id));
    } else {
      setInsuranceFeesToPayPayments([...insuranceFeesToPayPayments, periodicProductCheckbox]);
    }
  }

  const getPaymentSection = () => {
    if (insuranceFeesToPayPayments.length === 0) return <></>

    return (
      <SimpleGrid columns={[1, 1, 2]} spacing={8}>
        <GridItem>
          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="lg"
          >
            <VStack spacing={4} align="stretch">
              <HStack>
                <Icon as={FaMoneyBillWave} color="green.500" />
                <Heading as="h3" size="md" color={textColor}>
                  Seguro a abonar
                </Heading>
              </HStack>
              <Text color={secondaryTextColor}>
                Revisá atentamente. Vas a abonar los siguientes aranceles de seguro:
              </Text>
              <ProductsToPayList
                membershipFeesToPay={insuranceFeesToPayPayments}
                tableSize="sm"
                variant="default"
              />
            </VStack>
          </Box>
        </GridItem>
        <GridItem>
          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="lg"
            position="sticky"
            top={6}
          >
            <PaymentMethods
              productsToPayList={insuranceFeesToPayPayments}
              description="Podés abonar tu seguro pendiente de pago a través de los siguientes medios de pago. Es rápido, simple y seguro."
            />
          </Box>
        </GridItem>
      </SimpleGrid>
    )
  }

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        <Box
          bg={headerBg}
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
        >
          <HStack>
            <Icon as={GoShield} color="green.500" boxSize={6} />
            <Heading color={textColor}>Mi seguro</Heading>
          </HStack>
          <Text mt={2} color={secondaryTextColor}>
            {subtitleText}
          </Text>
        </Box>

        {isLoading ? (
          <LoadingSpinner label="Cargando tu seguro..." />
        ) : (
          <VStack spacing={6} align="stretch">
            {insuranceActivity && (
              <Box
                bg={cardBg}
                borderRadius="lg"
                border="1px solid"
                borderColor={borderColor}
                boxShadow="sm"
                overflow="hidden"
                p={6}
              >
                <VStack spacing={6} align="stretch">
                  <MemberActivityDetails activity={insuranceActivity} />
                  <ActivityPaymentHistory
                    activityId={insuranceActivity.id}
                    cardTitle="SEGURO"
                    activitiesFeesYears={activitiesFeesYears}
                    activitiesFeesPayments={activitiesFeesPayments}
                    feesToPayPayments={insuranceFeesToPayPayments}
                    onCheckboxChange={handleCheckboxChange}
                  />
                </VStack>
              </Box>
            )}

            {getPaymentSection()}
          </VStack>
        )}
      </VStack>
    </Container>
  )
}

export default MyInsurance;
